import { ComponentRef, Injectable, Injector, OnDestroy, inject } from "@angular/core";
import {
    ISnackbarOptions,
    LgSnackbarHorizontalPosition,
    LgSnackbarVerticalPosition
} from "./lg-snackbar.types";
import { GlobalPositionStrategy, Overlay, OverlayRef } from "@angular/cdk/overlay";
import { ComponentPortal } from "@angular/cdk/portal";
import { LgSnackbarComponent, LG_SNACKBAR_OPTIONS } from "./lg-snackbar.component";
import { atNextFrame } from "@logex/framework/utilities";
import { of, Subject } from "rxjs";
import { concatMap, debounceTime, delay, takeUntil, tap } from "rxjs/operators";

const DEFAULT_DELAY_HIDE = 3000;
const DEFAULT_WIDTH = 256;
const SEPARATION_DELAY = 500;
const MARGIN_OFFSET = 16;
const DEFAULT_HORIZONTAL_POSITION: LgSnackbarHorizontalPosition = "left";
const DEFAULT_VERTICAL_POSITION: LgSnackbarVerticalPosition = "bottom";

@Injectable({ providedIn: "root" })
export class LgSnackbarService implements OnDestroy {
    private _overlay = inject(Overlay);
    private _parentInjector = inject(Injector);

    constructor() {
        // Make delay before showing next snackbar
        let forceDelay = false;
        this._snackbarOptions$
            .pipe(
                concatMap(item => {
                    return forceDelay ? of(item).pipe(delay(SEPARATION_DELAY)) : of(item);
                }),
                tap(options => {
                    this._showAfterInterval(options);
                    forceDelay = true;
                }),
                debounceTime(SEPARATION_DELAY + 100),
                tap(() => {
                    forceDelay = false;
                }),
                takeUntil(this._destroyed$)
            )
            .subscribe();
    }

    private _snackbarOverlays: Array<{
        overlayRef: OverlayRef;
        componentRef: ComponentRef<LgSnackbarComponent>;
    }> = [];

    private _snackbarOptions$ = new Subject<ISnackbarOptions>();
    private _destroyed$ = new Subject<void>();

    show(options: ISnackbarOptions): void {
        this._snackbarOptions$.next(options);
    }

    hideAll(): void {
        this._snackbarOverlays.forEach(overlay => {
            overlay.componentRef.instance.hide();
        });
    }

    private _showAfterInterval(options: ISnackbarOptions): void {
        this._removeDetachedSnackbars();

        const horizontalPosition = options.horizontalPosition ?? DEFAULT_HORIZONTAL_POSITION;
        const verticalPosition = options.verticalPosition ?? DEFAULT_VERTICAL_POSITION;
        const horizontalOffset = options.horizontalOffset;
        const overlayRef = this._overlay.create({
            positionStrategy: this._getPositionStrategy(
                this._overlay,
                horizontalPosition,
                verticalPosition,
                undefined,
                horizontalOffset
            ),
            width: options.width ?? DEFAULT_WIDTH
        });

        const injector = Injector.create({
            parent: this._parentInjector,
            providers: [
                { provide: LG_SNACKBAR_OPTIONS, useValue: options },
                { provide: OverlayRef, useValue: overlayRef }
            ]
        });
        const portal = new ComponentPortal(LgSnackbarComponent, null, injector);
        const componentRef = overlayRef.attach(portal);

        if (options.autoHide !== false) {
            this._hide(componentRef.instance, options.delayHide ?? DEFAULT_DELAY_HIDE);
        }

        atNextFrame(() => {
            this._moveOldSnackbars(
                overlayRef.overlayElement.getBoundingClientRect().height,
                horizontalPosition,
                verticalPosition,
                horizontalOffset
            );
            this._snackbarOverlays.push({ overlayRef, componentRef });
        });
    }

    // eslint-disable-next-line @typescript-eslint/no-shadow
    private _hide(component: LgSnackbarComponent, delay: number): void {
        setTimeout(() => component.hide(), delay);
    }

    private _moveOldSnackbars(
        offset: number,
        horizontalPosition: LgSnackbarHorizontalPosition,
        verticalPosition: LgSnackbarVerticalPosition,
        horizontalOffset?: number
    ): void {
        this._snackbarOverlays.forEach(overlay => {
            const snackbarBoundaries = overlay.overlayRef.overlayElement.getBoundingClientRect();
            const snackbarHeight = snackbarBoundaries.height;
            const snackbarTop = snackbarBoundaries.top;
            const snackbarBottom = window.innerHeight - snackbarBoundaries.bottom;
            const [snackbarStart, snackbarEnd] =
                verticalPosition === "bottom"
                    ? [snackbarTop, snackbarBottom]
                    : [snackbarBottom, snackbarTop];
            if (snackbarStart - snackbarHeight > 0) {
                overlay.overlayRef.updatePositionStrategy(
                    this._getPositionStrategy(
                        this._overlay,
                        horizontalPosition,
                        verticalPosition,
                        snackbarEnd + offset,
                        horizontalOffset
                    )
                );
                overlay.componentRef.instance.moveUp(
                    verticalPosition === "bottom" ? offset : -offset
                );
            } else {
                overlay.overlayRef.updatePositionStrategy(
                    this._getPositionStrategy(
                        this._overlay,
                        horizontalPosition,
                        verticalPosition,
                        window.innerHeight - (MARGIN_OFFSET + snackbarHeight),
                        horizontalOffset
                    )
                );
            }
        });
    }

    private _getPositionStrategy(
        overlay: Overlay,
        horizontalPosition: LgSnackbarHorizontalPosition,
        verticalPosition: LgSnackbarVerticalPosition,
        verticalOffset?: number,
        horizontalOffset?: number
    ): GlobalPositionStrategy {
        const position = overlay.position().global();
        const hOffset = horizontalOffset ? `${horizontalOffset}px` : undefined;

        switch (horizontalPosition) {
            case "left":
                position.left(hOffset);
                break;
            case "right":
                position.right(hOffset);
                break;
            case "center":
                position.centerHorizontally(hOffset);
                break;
        }

        switch (verticalPosition) {
            case "top":
                position.top(verticalOffset ? `${verticalOffset}px` : undefined);
                break;
            case "bottom":
                position.bottom(verticalOffset ? `${verticalOffset}px` : undefined);
                break;
        }

        return position;
    }

    _removeDetachedSnackbars(): void {
        this._snackbarOverlays = this._snackbarOverlays.filter(overlay =>
            overlay.overlayRef.hasAttached()
        );
    }

    ngOnDestroy(): void {
        this._destroyed$.next();
        this._destroyed$.complete();
    }
}
